import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from "sonner";
import axios from "axios";
import LandingPage from "./pages/LandingPage";
import Login from "./Login";
import Impressum from "./pages/Impressum";
import Datenschutz from "./pages/Datenschutz";
import VerifyEmail from "./pages/VerifyEmail";
import Admin from "./pages/Admin";
import PaymentSuccess from "./PaymentSuccess";
import CreditPurchase from "./components/CreditPurchase";
import Tracking from "./components/Tracking";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Building2, MapPin, Phone, Mail, Globe, Star, Download, Trash2, Search, Plus, Filter, Award, Target, Loader2, LogOut, CreditCard, Wallet } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [showCreditPurchaseDialog, setShowCreditPurchaseDialog] = useState(false);
  const [isLoadingPayment, setIsLoadingPayment] = useState(false);
  
  const [bundeslaender, setBundeslaender] = useState([]);
  const [selectedState, setSelectedState] = useState(null);
  const [cities, setCities] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [actualityFilter, setActualityFilter] = useState("all");
  const [bundeslandImportJob, setBundeslandImportJob] = useState(null);
  const [formData, setFormData] = useState({
    company_name: "",
    address: "",
    phone: "",
    email: "",
    website: "",
    city: "",
    industry: "",
  });

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem("access_token");
    const userStr = localStorage.getItem("user");
    
    if (!token || !userStr) {
      navigate("/login");
      return;
    }
    
    setUser(JSON.parse(userStr));
    loadBundeslaenderAndIndustries();
  }, []);
  
  useEffect(() => {
    // Refresh user data periodically
    const interval = setInterval(() => {
      refreshUserData();
    }, 30000); // Every 30 seconds
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (selectedState) {
      loadCitiesForState(selectedState);
    }
  }, [selectedState]);

  useEffect(() => {
    // Auto-load contacts whenever any filter changes (works without state too).
    // Initial mount loads first page so user sees data immediately.
    loadContacts();
  }, [selectedState, selectedCities, selectedIndustries, actualityFilter]);

  useEffect(() => {
    // Poll for Bundesland import job status
    if (selectedState && bundeslandImportJob && bundeslandImportJob.status === 'running') {
      const interval = setInterval(async () => {
        try {
          const response = await axios.get(`${API}/import/bundesland/${encodeURIComponent(selectedState)}/status`);
          if (response.data.job) {
            setBundeslandImportJob(response.data.job);
            if (response.data.job.status !== 'running') {
              clearInterval(interval);
              loadContacts();
              toast.success(`Import abgeschlossen! ${response.data.job.total_contacts} Kontakte importiert.`);
            }
          }
        } catch (error) {
          console.error("Error polling bundesland import:", error);
        }
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [selectedState, bundeslandImportJob]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("access_token");
    return {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
  };
  
  const refreshUserData = async () => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) return;
      
      const response = await axios.get(`${API}/auth/me`, getAuthHeaders());
      const updatedUser = response.data;
      
      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));
    } catch (error) {
      console.error("Error refreshing user data:", error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };
  
  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    navigate("/login");
  };
  
  const loadPaymentPackages = async () => {
    // Deprecated - PayPal packages now loaded inside CreditPurchase dialog when opened
  };
  
  const handlePayment = async (packageId) => {
    setIsLoadingPayment(true);
    
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${API}/payments/checkout/session`,
        {
          package_id: packageId,
          origin_url: originUrl
        },
        getAuthHeaders()
      );
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Error creating checkout session:", error);
      toast.error("Fehler beim Erstellen der Zahlungssitzung");
      setIsLoadingPayment(false);
    }
  };
  
  const loadBundeslaenderAndIndustries = async () => {
    try {
      const [bundeslaenderRes, industriesRes] = await Promise.all([
        axios.get(`${API}/bundeslaender`),
        axios.get(`${API}/industries`)
      ]);
      console.log("Bundesländer loaded:", bundeslaenderRes.data);
      setBundeslaender(bundeslaenderRes.data.bundeslaender || []);
      setIndustries(industriesRes.data.industries || []);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Fehler beim Laden der Daten");
    }
  };

  const loadCitiesForState = async (state) => {
    try {
      const response = await axios.get(`${API}/bundeslaender/${encodeURIComponent(state)}/cities`);
      setCities(response.data.cities);
      setSelectedCities([]); // Reset city selection
      
      // Check for existing import job
      const jobResponse = await axios.get(`${API}/import/bundesland/${encodeURIComponent(state)}/status`);
      if (jobResponse.data.job && jobResponse.data.job.status === 'running') {
        setBundeslandImportJob(jobResponse.data.job);
      } else {
        setBundeslandImportJob(null);
      }
    } catch (error) {
      console.error("Error loading cities:", error);
      toast.error("Fehler beim Laden der Städte");
    }
  };

  const startExtendedImport = async (state) => {
    try {
      const response = await axios.post(
        `${API}/import/bundesland/${encodeURIComponent(state)}/extended/start`,
        {},
        getAuthHeaders()
      );
      
      toast.success(`✅ ${response.data.message}! 
        ${response.data.total_municipalities} Gemeinden werden verarbeitet.
        EasyCron muss den Batch-Endpoint aufrufen.`);
      
      // Show info about cronjob setup
      toast.info(`⚙️ Stelle sicher, dass EasyCron den Endpoint aufruft:
        POST ${API}/import/bundesland/${state}/batch`, {
        duration: 10000
      });
      
      // Check status
      await loadCitiesForState(state);
    } catch (error) {
      console.error("Error starting extended import:", error);
      const message = error.response?.data?.detail || "Fehler beim Starten des erweiterten Imports";
      toast.error(message);
    }
  };
  
  const startBundeslandCompleteImport = async (state) => {
    try {
      const response = await axios.post(`${API}/import/bundesland/${encodeURIComponent(state)}/complete`);
      toast.success(response.data.message);
      
      // Wait a bit for job to be created
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Get initial job status
      const jobResponse = await axios.get(`${API}/import/bundesland/${encodeURIComponent(state)}/status`);
      if (jobResponse.data.job) {
        setBundeslandImportJob(jobResponse.data.job);
      }
    } catch (error) {
      console.error("Error starting bundesland import:", error);
      if (error.response && error.response.status === 409) {
        toast.error("Import läuft bereits für dieses Bundesland");
      } else {
        toast.error("Fehler beim Starten des Imports");
      }
    }
  };

  const loadContacts = async () => {
    setContactsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", "100");

      if (selectedState) params.append("state", selectedState);
      selectedCities.forEach((c) => params.append("city", c));
      selectedIndustries.forEach((i) => params.append("industry", i));

      if (actualityFilter !== "all") {
        const days = actualityFilter === "3months" ? 90 : 180;
        params.set("days", days);
      }

      const response = await axios.get(`${API}/contacts?${params.toString()}`);
      // Backend now returns { total, returned, contacts } — keep backward-compat for arrays
      if (Array.isArray(response.data)) {
        setContacts(response.data);
        setContactsTotal(response.data.length);
      } else {
        setContacts(response.data.contacts || []);
        setContactsTotal(response.data.total || 0);
      }
    } catch (error) {
      console.error("Error loading contacts:", error);
    } finally {
      setContactsLoading(false);
    }
  };

  const handleCityToggle = (city) => {
    setSelectedCities(prev => 
      prev.includes(city) ? prev.filter(c => c !== city) : [...prev, city]
    );
  };

  const handleIndustryToggle = (industry) => {
    setSelectedIndustries(prev => 
      prev.includes(industry) ? prev.filter(i => i !== industry) : [...prev, industry]
    );
  };

  const exportToCSV = async () => {
    // Check credits
    if (!user || user.credits < 1) {
      toast.error("Nicht genug Credits! Bitte kaufen Sie Credits, um zu exportieren.");
      // setShowPaymentDialog(true);  // Will be PayPal later
      return;
    }
    
    // Count how many contacts will be exported (use TOTAL from server, not visible page)
    const contactCount = contactsTotal;
    
    if (contactCount === 0) {
      toast.error("Keine Kontakte zum Exportieren gefunden!");
      return;
    }
    
    // Check if user has enough credits
    if (user.credits < contactCount) {
      toast.error(`Nicht genug Credits! Sie benötigen ${contactCount} Credits für ${contactCount} Kontakte. Sie haben nur ${user.credits} Credits.`);
      // setShowPaymentDialog(true);  // Will be PayPal later
      return;
    }
    
    // Confirm export
    const confirmExport = window.confirm(
      `Sie exportieren ${contactCount} Kontakt(e).\n\n` +
      `Dies kostet ${contactCount} Credit(s).\n` +
      `Nach dem Export haben Sie noch ${user.credits - contactCount} Credit(s).\n\n` +
      `Möchten Sie fortfahren?`
    );
    
    if (!confirmExport) {
      return;
    }
    
    try {
      // Build query parameters - support multi-city/industry/state
      const params = new URLSearchParams();
      if (selectedState) params.append("state", selectedState);
      selectedCities.forEach((c) => params.append("city", c));
      selectedIndustries.forEach((i) => params.append("industry", i));
      if (actualityFilter !== "all") {
        params.set("days", actualityFilter === "3months" ? 90 : 180);
      }

      const response = await axios.get(`${API}/contacts/export/csv?${params.toString()}`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'adloc_leads.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`CSV erfolgreich heruntergeladen! ${contactCount} Credit(s) wurden abgezogen.`);
      
      // Refresh user data to update credits
      await refreshUserData();
    } catch (error) {
      console.error("Error exporting CSV:", error);
      const message = error.response?.data?.detail || "Fehler beim Exportieren";
      toast.error(message);
    }
  };

  // MailerLite upload function removed - use CSV export instead
  // This prevents sending unwanted confirmation emails to businesses

  const deleteAllContacts = async () => {
    if (!window.confirm("Möchten Sie wirklich alle Kontakte löschen?")) {
      return;
    }

    try {
      const response = await axios.delete(`${API}/contacts`);
      toast.success(`${response.data.deleted} Kontakte gelöscht`);
      setContacts([]);
      setSelectedContacts([]);
    } catch (error) {
      console.error("Error deleting contacts:", error);
      toast.error("Fehler beim Löschen");
    }
  };

  const toggleContactSelection = (contactId) => {
    setSelectedContacts(prev => 
      prev.includes(contactId) ? prev.filter(id => id !== contactId) : [...prev, contactId]
    );
  };

  const selectAllContacts = () => {
    if (selectedContacts.length === contacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(contacts.map(c => c.id));
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddContact = async () => {
    if (!formData.company_name || !formData.city || !formData.industry) {
      toast.error("Bitte mindestens Firmenname, Stadt und Branche eingeben");
      return;
    }

    try {
      await axios.post(`${API}/contacts/manual`, formData);
      toast.success(`${formData.company_name} erfolgreich hinzugefügt!`);
      
      setFormData({
        company_name: "",
        address: "",
        phone: "",
        email: "",
        website: "",
        city: "",
        industry: "",
      });
      setShowAddForm(false);
      loadContacts();
    } catch (error) {
      console.error("Error adding contact:", error);
      toast.error("Fehler beim Hinzufügen des Kontakts");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF3E0]">
      {/* Header - Adloc Branding */}
      <header className="bg-white/90 backdrop-blur-2xl border-b border-gray-200/60 shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Adloc Logo */}
              <img 
                src="https://customer-assets.emergentagent.com/job_german-bizleads/artifacts/tvr4e56b_2.png" 
                alt="Adloc Logo" 
                className="h-10 w-auto"
              />
              <div className="hidden sm:block">
                <p className="text-xs text-gray-600 font-medium">B2B-Kontaktdatenbank</p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {/* Credits Display */}
              <div className="flex items-center gap-2 bg-gradient-to-r from-[#2835F7] to-[#1a25c9] px-4 py-2 rounded-full shadow-lg">
                <Wallet className="w-4 h-4 text-white" />
                <span className="text-sm font-bold text-white">{user?.credits || 0} Credits</span>
              </div>
              
              {/* Credits kaufen Button */}
              <Button
                onClick={() => setShowCreditPurchaseDialog(true)}
                className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-bold rounded-full px-5 py-2 text-sm shadow-lg hover:shadow-xl transition-all hover:scale-105"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Credits kaufen
              </Button>
              
              <Dialog open={showAddForm} onOpenChange={setShowAddForm}>
                <DialogTrigger asChild>
                  <Button className="bg-white hover:bg-gray-50 text-gray-900 font-semibold rounded-full px-5 py-2 text-sm border-2 border-gray-200 shadow-sm hover:shadow-md transition-all hover:scale-105">
                    <Plus className="w-4 h-4 mr-2" />
                    <span className="hidden sm:inline">Kontakt hinzufügen</span>
                    <span className="sm:hidden">+</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-2xl">Neuen Kontakt manuell hinzufügen</DialogTitle>
                    <DialogDescription className="text-base">
                      Tragen Sie die Firmendaten ein
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="grid gap-4 py-4">
                    <div className="grid gap-2">
                      <Label htmlFor="company_name" className="text-sm font-semibold">Firmenname *</Label>
                      <Input
                        id="company_name"
                        placeholder="z.B. Elektro Müller GmbH"
                        value={formData.company_name}
                        onChange={(e) => handleFormChange("company_name", e.target.value)}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="grid gap-2">
                        <Label htmlFor="city" className="text-sm font-semibold">Stadt *</Label>
                        <Select value={formData.city} onValueChange={(value) => handleFormChange("city", value)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Stadt auswählen" />
                          </SelectTrigger>
                          <SelectContent position="popper" side="bottom" sideOffset={5} className="max-h-[300px]">
                            {cities.map(city => (
                              <SelectItem key={city} value={city}>{city}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="grid gap-2">
                        <Label htmlFor="industry" className="text-sm font-semibold">Branche *</Label>
                        <Select value={formData.industry} onValueChange={(value) => handleFormChange("industry", value)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Branche auswählen" />
                          </SelectTrigger>
                          <SelectContent position="popper" side="bottom" sideOffset={5} className="max-h-[300px]">
                            {industries.map(industry => (
                              <SelectItem key={industry} value={industry}>{industry}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="address" className="text-sm font-semibold">Adresse</Label>
                      <Input
                        id="address"
                        placeholder="z.B. Hauptstraße 15, 56068 Koblenz"
                        value={formData.address}
                        onChange={(e) => handleFormChange("address", e.target.value)}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="grid gap-2">
                        <Label htmlFor="phone" className="text-sm font-semibold">Telefon</Label>
                        <Input
                          id="phone"
                          placeholder="z.B. 0261 123456"
                          value={formData.phone}
                          onChange={(e) => handleFormChange("phone", e.target.value)}
                        />
                      </div>

                      <div className="grid gap-2">
                        <Label htmlFor="email" className="text-sm font-semibold">E-Mail</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="z.B. info@firma.de"
                          value={formData.email}
                          onChange={(e) => handleFormChange("email", e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="website" className="text-sm font-semibold">Website</Label>
                      <Input
                        id="website"
                        placeholder="z.B. https://www.firma.de"
                        value={formData.website}
                        onChange={(e) => handleFormChange("website", e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setShowAddForm(false)}>
                      Abbrechen
                    </Button>
                    <Button onClick={handleAddContact} className="bg-green-600 hover:bg-green-700">
                      Kontakt speichern
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              <Badge className="bg-white text-gray-900 text-sm px-3 py-1.5 font-semibold border-2 border-gray-200 shadow-sm">
                {contacts.length} Kontakte
              </Badge>
              
              {/* User Menu */}
              <div className="flex items-center gap-2 px-3 py-1.5 bg-[#2835F7] hover:bg-[#1a25c9] rounded-full transition-colors shadow-md">
                <div className="w-7 h-7 bg-white rounded-full flex items-center justify-center">
                  <span className="text-[#2835F7] font-bold text-xs">{user?.name?.charAt(0) || 'U'}</span>
                </div>
                <span className="text-white text-sm font-medium hidden sm:inline">{user?.name}</span>
              </div>
              
              {/* Logout Button */}
              <Button
                onClick={handleLogout}
                variant="outline"
                className="bg-white hover:bg-red-50 text-gray-700 hover:text-red-600 font-medium rounded-full px-4 py-2 text-sm border-2 border-gray-200 hover:border-red-200 transition-all"
              >
                <LogOut className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Abmelden</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 sm:px-6 py-8">
        {/* Step 1: Bundesland Selection */}
        <Card className="shadow-lg mb-6 border-2 border-gray-200 bg-white rounded-2xl overflow-hidden hover:shadow-xl transition-shadow duration-300">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <CardTitle className="text-2xl flex items-center gap-3 text-gray-900 font-bold">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#2835F7] to-[#1a25c9] rounded-xl flex items-center justify-center shadow-lg">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                  Bundesland wählen
                </CardTitle>
                <CardDescription className="text-sm text-gray-600 font-normal mt-2">Wählen Sie ein Bundesland für die Lead-Suche</CardDescription>
              </div>
              {selectedState && (
                <div className="flex gap-2 items-center">
                  <Button
                    onClick={() => startExtendedImport(selectedState)}
                    className="bg-green-600 hover:bg-green-700 text-white font-bold rounded-full px-5 py-2 text-sm shadow-lg hover:shadow-xl transition-all border-0 hover:scale-105"
                    disabled={bundeslandImportJob && bundeslandImportJob.status === 'running'}
                  >
                    <Target className="w-4 h-4 mr-2" />
                    {bundeslandImportJob && bundeslandImportJob.status === 'running' 
                      ? "Import läuft..." 
                      : `${selectedState} importieren`}
                  </Button>
                  <Badge className="bg-blue-50 text-blue-700 border border-blue-200 px-3 py-1.5 text-xs font-semibold">
                    200+ Gemeinden
                  </Badge>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {bundeslaender.map(state => (
                <Button
                  key={state}
                  className={`min-w-[140px] h-auto py-2.5 px-4 text-sm font-semibold whitespace-normal text-center transition-all rounded-full shadow-sm hover:shadow-md ${
                    selectedState === state 
                      ? "bg-gradient-to-r from-[#2835F7] to-[#1a25c9] text-white hover:from-[#1a25c9] hover:to-[#0d1570] border-0" 
                      : "bg-white border-2 border-gray-200 text-gray-900 hover:bg-gray-50 hover:border-[#2835F7]"
                  }`}
                  onClick={() => setSelectedState(state)}
                >
                  {state}
                </Button>
              ))}
            </div>
            
            {/* Bundesland Import Status */}
            {selectedState && bundeslandImportJob && (
              <div className="mt-6">
                {bundeslandImportJob.status === 'running' ? (
                  <div className="space-y-3 bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                      <span className="text-base font-semibold text-blue-700">
                        {selectedState} wird importiert...
                      </span>
                    </div>
                    <Progress value={bundeslandImportJob.progress} className="h-3 bg-blue-100" />
                    <div className="text-sm text-gray-700 space-y-1.5">
                      <p>📍 <strong>Aktuell:</strong> {bundeslandImportJob.current_city} / {bundeslandImportJob.current_industry}</p>
                      <p>📊 <strong>Fortschritt:</strong> {bundeslandImportJob.progress}%</p>
                      <p>✅ <strong>Kontakte:</strong> {bundeslandImportJob.total_contacts} (davon {bundeslandImportJob.contacts_with_email} mit E-Mail)</p>
                      <p className="text-xs text-gray-600 mt-2">
                        Geschätzt: ~{bundeslandImportJob.total_cities * 10 * 30} Kontakte für {selectedState}
                      </p>
                    </div>
                  </div>
                ) : bundeslandImportJob.status === 'completed' ? (
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <p className="text-green-800 font-semibold text-base">✅ Import abgeschlossen!</p>
                    <p className="text-sm text-green-700 mt-1">
                      {bundeslandImportJob.total_contacts} Kontakte importiert für {selectedState}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setBundeslandImportJob(null)}
                      className="mt-2"
                    >
                      OK
                    </Button>
                  </div>
                ) : null}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Filters Panel - Clean White */}
          <div className="lg:col-span-1 space-y-6">
            {/* Cities Card */}
            {selectedState && (
              <Card className="shadow-sm transition-all border border-[#D2D2D7] bg-white hover:shadow-md">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base text-[#1D1D1F] font-semibold">
                    <MapPin className="w-4 h-4 text-[#3B82F6]" />
                    Städte in {selectedState}
                  </CardTitle>
                  <CardDescription className="text-xs text-[#86868B] font-normal">{cities.length} Städte verfügbar</CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-80">
                    <div className="space-y-1">
                      {cities.map(city => (
                        <div 
                          key={city} 
                          className={`flex items-center space-x-3 p-3 rounded-lg transition-all cursor-pointer ${
                            selectedCities.includes(city) 
                              ? 'bg-blue-50 border-l-4 border-blue-600 shadow-sm' 
                              : 'hover:bg-slate-50 border-l-4 border-transparent'
                          }`}
                        >
                          <Checkbox
                            id={`city-${city}`}
                            checked={selectedCities.includes(city)}
                            onCheckedChange={() => handleCityToggle(city)}
                            className="border-slate-400"
                          />
                          <label
                            htmlFor={`city-${city}`}
                            className="text-sm font-semibold text-slate-700 cursor-pointer flex-1"
                          >
                            {city}
                          </label>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            )}

            {/* Industries Card */}
            <Card className="shadow-xl transition-all border-0 bg-white/95 backdrop-blur-xl hover:shadow-2xl">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg text-slate-900 font-bold">
                  <Building2 className="w-5 h-5 text-indigo-600" />
                  Branchen auswählen
                </CardTitle>
                <CardDescription className="text-sm text-slate-600 font-semibold">{industries.length} Branchen verfügbar</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-80">
                  <div className="space-y-1">
                    {industries.map(industry => (
                      <div 
                        key={industry} 
                        className={`flex items-center space-x-3 p-3 rounded-lg transition-all cursor-pointer ${
                          selectedIndustries.includes(industry) 
                            ? 'bg-indigo-50 border-l-4 border-indigo-600 shadow-sm' 
                            : 'hover:bg-slate-50 border-l-4 border-transparent'
                        }`}
                      >
                        <Checkbox
                          id={`industry-${industry}`}
                          checked={selectedIndustries.includes(industry)}
                          onCheckedChange={() => handleIndustryToggle(industry)}
                          className="border-slate-400"
                        />
                        <label
                          htmlFor={`industry-${industry}`}
                          className="text-sm font-semibold text-slate-700 cursor-pointer flex-1"
                        >
                          {industry}
                        </label>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Actuality Filter */}
            <Card className="shadow-xl transition-all border-2 border-emerald-500 bg-gradient-to-br from-emerald-50 to-teal-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2 text-slate-900 font-bold">
                  <Filter className="w-5 h-5 text-emerald-600" />
                  Aktualitätsfilter
                </CardTitle>
                <CardDescription className="text-xs text-slate-600 font-semibold">Filtern nach Datenfrische</CardDescription>
              </CardHeader>
              <CardContent>
                <Select value={actualityFilter} onValueChange={setActualityFilter}>
                  <SelectTrigger className="bg-white border-2 border-emerald-300 hover:border-emerald-500 text-slate-700 font-semibold">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent position="popper" side="bottom" sideOffset={5}>
                    <SelectItem value="all">✨ Alle Kontakte</SelectItem>
                    <SelectItem value="3months">🔥 Letzte 3 Monate</SelectItem>
                    <SelectItem value="6months">⏰ Letzte 6 Monate</SelectItem>
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>
          </div>

          {/* Results Panel - Clean White */}
          <div className="lg:col-span-2">
            <Card className="shadow-2xl border-0 bg-white/95 backdrop-blur-xl">
              <CardHeader className="pb-4 border-b border-slate-200">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <CardTitle className="text-2xl text-slate-900 font-black">Gefundene Kontakte</CardTitle>
                    <CardDescription className="text-base text-slate-600 font-semibold mt-1" data-testid="contacts-count-label">
                      {contactsLoading ? (
                        <span className="inline-flex items-center gap-2 text-slate-500">
                          <Loader2 className="w-4 h-4 animate-spin" /> Lädt...
                        </span>
                      ) : (
                        <>
                          <span className="text-blue-600 font-bold">{contactsTotal.toLocaleString("de-DE")}</span> Treffer
                          {contactsTotal > contacts.length && (
                            <span className="text-sm font-normal text-slate-500 ml-2">
                              (zeige {contacts.length})
                            </span>
                          )}
                        </>
                      )}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2 flex-wrap items-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={selectAllContacts}
                      className="border-2 border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-400 font-semibold"
                    >
                      {selectedContacts.length === contacts.length ? "Alle abwählen" : "Alle auswählen"}
                    </Button>
                    <Button
                      size="sm"
                      onClick={exportToCSV}
                      disabled={contactsTotal === 0 || contactsLoading}
                      data-testid="csv-export-btn"
                      className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold border-0 shadow-lg"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      CSV Export ({contactsTotal.toLocaleString("de-DE")} {contactsTotal === 1 ? 'Credit' : 'Credits'})
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={deleteAllContacts}
                      disabled={contacts.length === 0}
                      className="border-2 border-red-300 bg-white text-red-600 hover:bg-red-50 hover:border-red-400 font-semibold"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Löschen
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[calc(100vh-320px)]">
                  <div className="space-y-3">
                    {contacts.length === 0 ? (
                      <div className="text-center py-16 text-gray-500">
                        <Search className="w-16 h-16 mx-auto mb-4 opacity-20" />
                        <p className="text-lg font-semibold">Keine Kontakte gefunden</p>
                        <p className="text-sm mt-1">Wählen Sie ein Bundesland, Städte und Branchen aus</p>
                      </div>
                    ) : (
                      contacts.map((contact, idx) => (
                        <div key={contact.id || idx}>
                          <div className={`flex items-start gap-4 p-5 rounded-xl transition-all cursor-pointer border-2 ${
                            contact.is_sponsored 
                              ? 'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-400 hover:border-amber-500 hover:shadow-xl' 
                              : 'bg-white hover:bg-blue-50 border-slate-200 hover:border-blue-300 hover:shadow-lg'
                          }`}>
                            <Checkbox
                              checked={selectedContacts.includes(contact.id)}
                              onCheckedChange={() => toggleContactSelection(contact.id)}
                              className="mt-1.5"
                            />
                            <div className="flex-1 space-y-2">
                              <div className="flex items-start justify-between">
                                <div>
                                  {contact.is_sponsored && (
                                    <Badge className="mb-2 bg-gradient-to-r from-yellow-500 to-amber-500 text-white">
                                      <Award className="w-3 h-3 mr-1" />
                                      GESPONSERT
                                    </Badge>
                                  )}
                                  <h3 className="font-bold text-gray-900 text-base">{contact.company_name}</h3>
                                  <div className="flex items-center gap-2 mt-1.5">
                                    <Badge variant="secondary" className="text-xs font-semibold">{contact.city}</Badge>
                                    <Badge variant="outline" className="text-xs font-semibold">{contact.industry}</Badge>
                                  </div>
                                </div>
                                {contact.rating && (
                                  <div className="flex items-center gap-1.5 text-amber-500">
                                    <Star className="w-4 h-4 fill-current" />
                                    <span className="text-sm font-bold">{contact.rating}</span>
                                    {contact.reviews_count && (
                                      <span className="text-xs text-gray-500 font-semibold">({contact.reviews_count})</span>
                                    )}
                                  </div>
                                )}
                              </div>
                              
                              <div className="grid grid-cols-2 gap-3 text-sm">
                                {contact.address && (
                                  <div className="flex items-center gap-2 text-gray-600">
                                    <MapPin className="w-4 h-4 flex-shrink-0 text-blue-600" />
                                    <span className="truncate">{contact.address}</span>
                                  </div>
                                )}
                                {contact.phone && (
                                  <div className="flex items-center gap-2 text-gray-600">
                                    <Phone className="w-4 h-4 flex-shrink-0 text-green-600" />
                                    <span>{contact.phone}</span>
                                  </div>
                                )}
                                {contact.email && (
                                  <div className="flex items-center gap-2 text-gray-600">
                                    <Mail className="w-4 h-4 flex-shrink-0 text-purple-600" />
                                    <span className="truncate">{contact.email}</span>
                                  </div>
                                )}
                                {contact.website && (
                                  <div className="flex items-center gap-2 text-blue-600">
                                    <Globe className="w-4 h-4 flex-shrink-0" />
                                    <a 
                                      href={contact.website} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="truncate hover:underline font-medium"
                                    >
                                      Website
                                    </a>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          {idx < contacts.length - 1 && <Separator className="my-2" />}
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Footer - Premium Dark */}
      <footer className="bg-slate-900/95 backdrop-blur-xl border-t border-slate-700/50 mt-12 py-8">
        <div className="container mx-auto px-6">
          <div className="flex flex-col items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#3B82F6] rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-white text-lg font-black">Ad</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#1D1D1F]">
                  Adloc
                </h3>
                <p className="text-xs text-[#86868B]">Lokale B2B-Leads</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 text-slate-400 text-sm">
              <a 
                href="https://www.pixel56.de" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-blue-400 transition-colors flex items-center gap-1 font-semibold"
              >
                <Globe className="w-4 h-4" />
                pixel56.de
              </a>
              <span>•</span>
              <span className="font-semibold">Powered by Pixel56</span>
            </div>
            
            <p className="text-center text-xs text-[#86868B]">
              © 2025 Adloc - Lokale B2B-Leads für Deutschland
            </p>
          </div>
        </div>
      </footer>

      {/* Credit Purchase Dialog */}
      <CreditPurchase 
        open={showCreditPurchaseDialog}
        onClose={() => setShowCreditPurchaseDialog(false)}
        onSuccess={() => refreshUserData()}
      />
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem("access_token");
  return token ? children : <Navigate to="/login" />;
};

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <Tracking />
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/impressum" element={<Impressum />} />
            <Route path="/datenschutz" element={<Datenschutz />} />
            <Route path="/login" element={<Login />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/payment-success" element={
              <ProtectedRoute>
                <PaymentSuccess />
              </ProtectedRoute>
            } />
            <Route path="/app" element={
              <ProtectedRoute>
                <Home />
              </ProtectedRoute>
            } />
          </Routes>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
